import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { BaseService } from '../base.service';
import { UserAuthService } from '../auth.service';
import { SPECTRUM_API_PATH, SPECTRUM_AUTH_API } from '../tokens';

export interface SpectrumModule {
    moduleName: string;
    route: string;
    image?: string;
    /** Set on Group Admin entries: launch the module scoped to this group. */
    groupCode?: string;
    groupName?: string;
}

interface GroupAppAccess {
    orgCode?: string;
    orgName?: string;
}

export interface SpectrumModuleIcon {
    /** Font Awesome 6 classes, e.g. "fas fa-graduation-cap". */
    icon: string;
    /** Chip foreground color. */
    color: string;
    /** Chip background (soft tint of the foreground). */
    bg: string;
}

/**
 * Purpose-matched icon per module, keyed on the names `security/modules`
 * actually returns. Icons replace the old S3 image URLs so every module
 * renders the same way the in-app menu icons do — a Font Awesome glyph on a
 * soft color chip. Matching is exact-first, then by keyword, so backend
 * naming variants ("Admissions" vs "Admissions CRM") land on the same icon.
 */
const MODULE_ICONS: Record<string, SpectrumModuleIcon> = {
    'dashboard':        { icon: 'fas fa-gauge-high',          color: '#4f46e5', bg: '#eef2ff' },
    'institute':        { icon: 'fas fa-building-columns',    color: '#0369a1', bg: '#e0f2fe' },
    'students':         { icon: 'fas fa-graduation-cap',      color: '#2563eb', bg: '#dbeafe' },
    'hr':               { icon: 'fas fa-id-badge',            color: '#7c3aed', bg: '#f3e8ff' },
    'finance':          { icon: 'fas fa-indian-rupee-sign',   color: '#16a34a', bg: '#dcfce7' },
    'academics':        { icon: 'fas fa-book-open',           color: '#d97706', bg: '#fef3c7' },
    'attendance':       { icon: 'fas fa-calendar-check',      color: '#0d9488', bg: '#ccfbf1' },
    'admissions':       { icon: 'fas fa-user-plus',           color: '#e11d48', bg: '#ffe4e6' },
    'lesson planning':  { icon: 'fas fa-chalkboard-user',     color: '#ea580c', bg: '#ffedd5' },
    'message center':   { icon: 'fas fa-comments',            color: '#0891b2', bg: '#cffafe' },
    'transport':        { icon: 'fas fa-bus',                 color: '#ca8a04', bg: '#fef9c3' },
    'documents':        { icon: 'fas fa-file-lines',          color: '#475569', bg: '#f1f5f9' },
    'calendar':         { icon: 'fas fa-calendar-days',       color: '#db2777', bg: '#fce7f3' },
    'settings':         { icon: 'fas fa-sliders',             color: '#52525b', bg: '#f4f4f5' },
    'system admin':     { icon: 'fas fa-screwdriver-wrench',  color: '#52525b', bg: '#f4f4f5' },
    'group admin':      { icon: 'fas fa-sitemap',             color: '#9333ea', bg: '#f3e8ff' },
    'security':         { icon: 'fas fa-shield-halved',       color: '#dc2626', bg: '#fee2e2' },
    'lms':              { icon: 'fas fa-laptop-file',         color: '#0284c7', bg: '#e0f2fe' },
    'progress path':    { icon: 'fas fa-chart-line',          color: '#059669', bg: '#d1fae5' },
    'exams':            { icon: 'fas fa-file-pen',            color: '#b45309', bg: '#fef3c7' },
    'library':          { icon: 'fas fa-book-bookmark',       color: '#92400e', bg: '#fef3c7' },
    'quote':            { icon: 'fas fa-file-invoice',        color: '#0f766e', bg: '#ccfbf1' },
    'payments':         { icon: 'fas fa-credit-card',         color: '#15803d', bg: '#dcfce7' },
    'hostel':           { icon: 'fas fa-bed',                 color: '#7c3aed', bg: '#f3e8ff' },
    'personalised products': { icon: 'fas fa-boxes-stacked',  color: '#c2410c', bg: '#ffedd5' },
};

const MODULE_ICON_KEYWORDS: Array<{ match: string; key: string }> = [
    { match: 'admission',  key: 'admissions' },
    { match: 'lesson',     key: 'lesson planning' },
    { match: 'message',    key: 'message center' },
    { match: 'group',      key: 'group admin' },
    { match: 'school',     key: 'institute' },
    { match: 'institute',  key: 'institute' },
    { match: 'student',    key: 'students' },
    { match: 'fee',        key: 'finance' },
    { match: 'finance',    key: 'finance' },
    { match: 'progress',   key: 'progress path' },
    { match: 'transport',  key: 'transport' },
    { match: 'document',   key: 'documents' },
    { match: 'exam',       key: 'exams' },
    { match: 'system',     key: 'system admin' },
    { match: 'setting',    key: 'settings' },
    { match: 'pay',        key: 'payments' },
    { match: 'hostel',     key: 'hostel' },
    { match: 'quote',      key: 'quote' },
    { match: 'product',    key: 'personalised products' },
];

const DEFAULT_MODULE_ICON: SpectrumModuleIcon = {
    icon: 'fas fa-cube', color: '#4f46e5', bg: '#eef2ff'
};

/**
 * The icon for a module name; falls back to a neutral cube for unknowns.
 *
 * A module rebuilt for v2 can come back named for the rebuild — "Dashboard V2",
 * "Dashboard-V2", "DashboardV2" are all the Dashboard — so the suffix is dropped
 * before matching rather than keyed for separately. (`sessions.component.ts`
 * resolves the home module the same way.) Names whose keyword already matched
 * through the suffix, like "Students V2", are unaffected.
 */
export function getModuleIcon(moduleName: string | undefined | null): SpectrumModuleIcon {
    const name = (moduleName || '').trim().toLowerCase().replace(/[\s_-]*v2$/, '').trim();
    if (!name) return DEFAULT_MODULE_ICON;
    if (MODULE_ICONS[name]) return MODULE_ICONS[name];
    const keyword = MODULE_ICON_KEYWORDS.find(entry => name.includes(entry.match));
    return (keyword && MODULE_ICONS[keyword.key]) || DEFAULT_MODULE_ICON;
}

/**
 * The list of Spectrum modules the logged-in user can reach, plus the
 * cross-module launch. Shared by the header bar and the sidebar switcher so
 * both show the same entries from a single `security/modules` call per app load.
 */
@Injectable({ providedIn: 'root' })
export class SpectrumModuleService {

    private auth = inject(UserAuthService);
    private base = inject(BaseService);
    private apiPath = inject(SPECTRUM_API_PATH);
    private authBase = inject(SPECTRUM_AUTH_API);

    private modules$?: Observable<SpectrumModule[]>;
    private myProfileRoute = '';

    getModules(): Observable<SpectrumModule[]> {
        if (!this.modules$) {
            this.modules$ = this.base.get<SpectrumModule[]>(`${this.apiPath}/security/modules`).pipe(
                switchMap(modules => this.decorate(modules || [])),
                catchError(() => of<SpectrumModule[]>([])),
                shareReplay(1)
            );
        }
        return this.modules$;
    }

    /** The module whose route matches the current origin, if any. */
    currentModule(modules: SpectrumModule[]): SpectrumModule | null {
        const origin = this.normalizeRoute(window.location.origin);
        return modules.find(module => origin === this.normalizeRoute(module.route)) || null;
    }

    isCurrent(module: SpectrumModule, modules: SpectrumModule[]): boolean {
        const current = this.currentModule(modules);
        return this.normalizeRoute(current?.route) === this.normalizeRoute(module.route)
            && current?.moduleName === module.moduleName;
    }

    /**
     * Whether a click asked for a new tab — Ctrl/Cmd-click or middle-click,
     * the same gesture a link honours. Middle-click also needs the browser's
     * autoscroll suppressed, which is why the event is consumed here.
     */
    wantsNewTab(event?: MouseEvent | null): boolean {
        if (!event) return false;
        if (event.button === 1) {
            event.preventDefault();
            return true;
        }
        return event.ctrlKey || event.metaKey;
    }

    /** The launch URL that hands the current session to `module`. */
    moduleUrl(module: SpectrumModule): string {
        if (!module.route) return '';
        const params = this.buildSessionParams();
        if (module.groupCode) {
            // Group Admin runs against the group, not the current school —
            // rewrite the session scope so its header and APIs use the group.
            params.set('code', module.groupCode);
            params.set('appName', 'GroupAdmin');
            if (module.groupName) {
                params.set('schoolName', module.groupName);
            }
        }
        return `${module.route}/validate?${params.toString()}`;
    }

    /**
     * Hands the current session to another module through its `/validate`
     * bootstrap. With `newTab` the module opens beside this one instead of
     * replacing it, so two modules can be worked in parallel; the session
     * params travel in the URL, so the new tab bootstraps its own session.
     * A blocked popup falls back to the same tab rather than doing nothing.
     */
    switchModule(module: SpectrumModule, newTab = false): void {
        const url = this.moduleUrl(module);
        if (!url) return;
        if (newTab) {
            // No `noopener` feature here: it makes `window.open` return null
            // even on success, which is indistinguishable from a blocked
            // popup. The handle is severed straight after instead.
            const opened = window.open(url, '_blank');
            if (opened) {
                opened.opener = null;
                return;
            }
        }
        window.location.href = url;
    }

    openMyProfile(): void {
        const route = this.myProfileRoute || `https://myprofile.${new URL(this.authBase).hostname}`;
        window.location.href = `${route}/validate?${this.buildSessionParams().toString()}`;
    }

    buildSessionParams(): URLSearchParams {
        const params = new URLSearchParams();
        const token = this.auth.getToken();
        const orgCode = this.auth.getOrgCode();
        const appName = this.auth.getAppName() || 'Spectrum';
        const employeeId = this.auth.getEmployeeId();
        const employeeName = this.auth.getEmployeeName();
        const schoolName = this.auth.getSchoolName();
        const userName = this.auth.getUserName();

        if (token) params.set('token', token);
        if (orgCode) params.set('code', orgCode);
        params.set('appName', appName);
        if (employeeId) params.set('employeeId', employeeId);
        if (employeeName) params.set('employeeName', employeeName);
        if (schoolName) params.set('schoolName', schoolName);
        if (userName) params.set('userName', userName);
        // Support-session tabs must stay pinned to their institute across
        // module switches — the destination validate re-reads this marker.
        if (this.auth.isSupportSession()) params.set('support', 'Y');

        return params;
    }

    normalizeRoute(route: string | undefined | null): string {
        return (route || '').trim().replace(/\/+$/, '');
    }

    /**
     * Confirmed dead or duplicate `APP_MODULE_SETUP` rows, verified 2026-10-20
     * by checking what each route actually serves — a v1 page is titled
     * plainly and serves an unhashed `main.js`; a v2 page is titled "Spectrum
     * <Module>" and serves a content-hashed bundle (`outputHashing: "all"`,
     * see this repo's CLAUDE.md "Deploy Cache Policy"). Each one slips past
     * `preferV2`'s generic `<Module> V2` match because its name doesn't
     * follow that convention:
     *  - "Academics-AAA" — the real v1 Academics tile, oddly renamed;
     *    "Academics V2" is the live v2 tile. (The plain "Academics" row is
     *    also stale — its route falls through to the portal's page rather
     *    than serving Academics at all — but it already loses to
     *    "Academics V2" under the generic rule, so it needs no entry here.)
     *  - "DocumentCenter" — v1; "Documents V2" is the live v2 tile. The
     *    backend reformats camelCase names before this ever reaches the
     *    browser (`UserSecurityServices.getApplicationModules`), so this
     *    actually arrives as "Document Center" — matched here by normalized
     *    key, not literal string, so either spelling is caught.
     *  - "Transport-New" — points at the exact same v1 host as plain
     *    "Transport" (newtransport.schooladminportal.com); "Transport V2" is
     *    the live tile.
     *  - "Admissions-New" — an exact duplicate of "Admissions", which already
     *    points at the live app (admissionscrm.schooladminportal.com); there
     *    is no v1 Admissions tile left in the table at all.
     * If APP_MODULE_SETUP is ever cleaned up to drop these rows outright,
     * this becomes a no-op and can be deleted.
     */
    private readonly KNOWN_STALE_MODULE_KEYS = new Set(
        ['Academics-AAA', 'DocumentCenter', 'Transport-New', 'Admissions-New'].map(name => this.normalize(name))
    );

    /** Lowercased with every space/hyphen/underscore stripped, so spacing and
     *  casing differences (camelCase splits, "V2" vs "-V2" vs "v2") never
     *  cause a name match to silently miss. */
    private normalize(name: string): string {
        return name.trim().toLowerCase().replace(/[\s_-]+/g, '');
    }

    private decorate(raw: SpectrumModule[]): Observable<SpectrumModule[]> {
        const profileModule = raw.find(module =>
            module.moduleName === 'MyProfile' || module.moduleName === 'My Profile'
        );
        this.myProfileRoute = profileModule?.route || `https://myprofile.${new URL(this.authBase).hostname}`;

        const modules = raw
            .filter(module => !['MyProfile', 'My Profile'].includes(module.moduleName))
            .map(module => ({
                ...module,
                moduleName: module.moduleName === 'School' ? 'Institute' : module.moduleName
            }));

        return this.gateGroupAdmin(this.preferV2(modules));
    }

    /**
     * `APP_MODULE_SETUP` has no v1/v2 flag (see akshar-spectrum's CLAUDE.md,
     * "Three modules, one WAR" migration notes): a v2 rollout either repoints
     * the existing row in place, leaving one entry, or adds a second row
     * alongside it ("Documents" + "Documents V2"), leaving both reachable.
     * This drops the v1 half of any such pair so the switcher shows one tile
     * per module. A module with a single row — whether that row is v1 (no v2
     * build yet, e.g. Library, LMS) or already repointed in place (e.g.
     * Academics) — is untouched; there is nothing to prefer it over.
     */
    private preferV2(modules: SpectrumModule[]): SpectrumModule[] {
        const isV2 = (name: string) => this.normalize(name).endsWith('v2');
        const baseName = (name: string) => this.normalize(name).replace(/v2$/, '');

        const live = modules.filter(module => !this.KNOWN_STALE_MODULE_KEYS.has(this.normalize(module.moduleName)));
        const v2BaseNames = new Set(
            live.filter(module => isV2(module.moduleName)).map(module => baseName(module.moduleName))
        );
        return live.filter(module => isV2(module.moduleName) || !v2BaseNames.has(baseName(module.moduleName)));
    }

    /**
     * Group Admin is not a school-scoped module: it must appear only when the
     * logged-in user actually belongs to at least one group — i.e. holds a
     * (GroupAdmin, groupCd) access row in auth. The row also supplies the
     * group code/name the launch needs (a school-scoped launch would start a
     * broken group session against the school code).
     */
    private gateGroupAdmin(modules: SpectrumModule[]): Observable<SpectrumModule[]> {
        const groupEntries = modules.filter(module => module.moduleName === 'Group Admin');
        if (groupEntries.length === 0) {
            return of(modules);
        }
        const template = groupEntries[0];
        const rest = modules.filter(module => module.moduleName !== 'Group Admin');

        const username = this.auth.getUserName();
        if (!username) {
            return of(rest);
        }
        const params = new URLSearchParams({ username, appName: 'GroupAdmin' });
        return this.base.get<GroupAppAccess[]>(`${this.authBase}/auth/user-access?${params.toString()}`).pipe(
            map(rows => {
                const groups = (rows || []).filter(row => row.orgCode);
                if (groups.length === 0) {
                    return rest;
                }
                const entries = groups.map(row => ({
                    ...template,
                    moduleName: groups.length > 1
                        ? `Group Admin — ${row.orgName || row.orgCode}`
                        : 'Group Admin',
                    groupCode: row.orgCode,
                    groupName: row.orgName
                }));
                return [...rest, ...entries];
            }),
            catchError(() => of(rest))
        );
    }
}
